import { useState, useEffect } from 'react'
import { useClientStore } from '../../stores/clientStore'
import { useCanAccess } from '../../hooks/useCanAccess'
import ClientIoTConfig from './ClientIoTConfig'
import ClientIoTStatus from './ClientIoTStatus'
import type { Client } from '../../types/client'

type ModalMode = 'create' | 'edit' | 'iot' | null

const EMPTY_CLIENT: Partial<Client> = {
  name: '',
  code: '',
  parent_id: null,
  iot_provider_type: 'thingsboard',
  iot_config: {},
  minio_prefix: null,
  is_active: true,
  metadata: {},
}

export default function ClientManagement() {
  const clients = useClientStore((s) => s.clients)
  const fetchClients = useClientStore((s) => s.fetchClients)
  const createClient = useClientStore((s) => s.createClient)
  const updateClient = useClientStore((s) => s.updateClient)
  const deleteClient = useClientStore((s) => s.deleteClient)

  const [modal, setModal] = useState<ModalMode>(null)
  const [editingClient, setEditingClient] = useState<Partial<Client>>(EMPTY_CLIENT)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [iotClientId, setIotClientId] = useState<string | null>(null)
  const [filterActive, setFilterActive] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const canCreate = useCanAccess('clients', 'create')
  const canUpdate = useCanAccess('clients', 'update')
  const canDelete = useCanAccess('clients', 'delete')

  useEffect(() => {
    if (clients.length === 0) fetchClients()
  }, [clients.length, fetchClients])

  const filtered = filterActive ? clients.filter((c) => c.is_active) : clients

  const openCreate = () => {
    setEditingClient({ ...EMPTY_CLIENT })
    setEditingId(null)
    setError(null)
    setModal('create')
  }

  const openEdit = (c: Client) => {
    setEditingClient({ ...c })
    setEditingId(c.id)
    setError(null)
    setModal('edit')
  }

  const openIoT = (c: Client) => {
    setIotClientId(c.id)
    setModal('iot')
  }

  const handleSave = async () => {
    setSaving(true)
    setError(null)
    try {
      if (modal === 'create') {
        await createClient(editingClient)
      } else if (modal === 'edit' && editingId) {
        await updateClient(editingId, editingClient)
      }
      setModal(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur sauvegarde')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (c: Client) => {
    if (!confirm(`Supprimer le client "${c.name}" ?`)) return
    try {
      await deleteClient(c.id)
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Erreur suppression')
    }
  }

  return (
    <div className="admin-section">
      <div className="admin-section__header">
        <h3>Clients</h3>
        <div className="admin-section__actions">
          <label className="admin-filter">
            <input
              type="checkbox"
              checked={filterActive}
              onChange={(e) => setFilterActive(e.target.checked)}
            />
            Actifs uniquement
          </label>
          {canCreate && (
            <button className="admin-btn admin-btn--create" onClick={openCreate}>
              Nouveau client
            </button>
          )}
        </div>
      </div>

      <table className="admin-table">
        <thead>
          <tr>
            <th>Nom</th>
            <th>Code</th>
            <th>Provider IoT</th>
            <th>Statut</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filtered.map((c) => (
            <tr key={c.id}>
              <td>{c.name}</td>
              <td><code>{c.code}</code></td>
              <td>{c.iot_provider_type}</td>
              <td>
                <span className={`admin-badge ${c.is_active ? 'admin-badge--green' : 'admin-badge--red'}`}>
                  {c.is_active ? 'Actif' : 'Inactif'}
                </span>
              </td>
              <td className="admin-table__actions">
                {canUpdate && <button className="admin-btn admin-btn--small" onClick={() => openEdit(c)}>Editer</button>}
                {canUpdate && <button className="admin-btn admin-btn--small" onClick={() => openIoT(c)}>IoT</button>}
                {canDelete && <button className="admin-btn admin-btn--small admin-btn--danger" onClick={() => handleDelete(c)}>Supprimer</button>}
              </td>
            </tr>
          ))}
          {filtered.length === 0 && (
            <tr><td colSpan={5} className="admin-table__empty">Aucun client</td></tr>
          )}
        </tbody>
      </table>

      {/* Modal create/edit */}
      {(modal === 'create' || modal === 'edit') && (
        <div className="admin-modal__overlay" onClick={() => setModal(null)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{modal === 'create' ? 'Nouveau client' : 'Editer client'}</h3>
            <div className="admin-form">
              <div className="admin-form__field">
                <label>Nom</label>
                <input
                  type="text"
                  value={editingClient.name ?? ''}
                  onChange={(e) => setEditingClient((p) => ({ ...p, name: e.target.value }))}
                />
              </div>
              <div className="admin-form__field">
                <label>Code</label>
                <input
                  type="text"
                  value={editingClient.code ?? ''}
                  onChange={(e) => setEditingClient((p) => ({ ...p, code: e.target.value }))}
                />
              </div>
              <div className="admin-form__field">
                <label>Parent</label>
                <select
                  value={editingClient.parent_id ?? ''}
                  onChange={(e) => setEditingClient((p) => ({ ...p, parent_id: e.target.value || null }))}
                >
                  <option value="">Aucun (racine)</option>
                  {clients
                    .filter((c) => c.id !== editingId)
                    .map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                </select>
              </div>
              <ClientIoTConfig
                providerType="thingsboard"
                config={editingClient.iot_config ?? {}}
                onChange={(config) => setEditingClient((p) => ({ ...p, iot_config: config }))}
              />
              <label className="admin-filter">
                <input
                  type="checkbox"
                  checked={editingClient.is_active ?? true}
                  onChange={(e) => setEditingClient((p) => ({ ...p, is_active: e.target.checked }))}
                />
                Actif
              </label>
              {error && <div className="admin-error">{error}</div>}
              <div className="admin-form__actions">
                <button className="admin-btn" onClick={() => setModal(null)}>Annuler</button>
                <button className="admin-btn admin-btn--create" onClick={handleSave} disabled={saving}>
                  {saving ? 'Sauvegarde...' : 'Sauvegarder'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal IoT status */}
      {modal === 'iot' && iotClientId && (
        <div className="admin-modal__overlay" onClick={() => setModal(null)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Statut IoT</h3>
            <ClientIoTStatus clientId={iotClientId} onClose={() => setModal(null)} />
          </div>
        </div>
      )}
    </div>
  )
}
